import { z } from 'zod';
import { BaseEntitySchema, TimestampSchema } from './common';
import { ImageId, SessionId, UserId } from './ids';

export const ImageEntityTypeSchema = z.enum([
  'player',
  'session',
  'game',
  'team',
  'round',
  'gallery',
  'group',
]);
export type ImageEntityType = z.infer<typeof ImageEntityTypeSchema>;

export const ImageUploadStatusSchema = z.enum(['pending', 'uploading', 'synced', 'failed']);
export type ImageUploadStatus = z.infer<typeof ImageUploadStatusSchema>;

// Note: Blob cannot be validated by Zod, so we use z.any() for runtime storage
// The TypeScript type ensures type safety at compile time
export const ImageRecordSchema = BaseEntitySchema.extend({
  id: ImageId,
  entityType: ImageEntityTypeSchema,
  entityId: z.string(), // PlayerId | SessionId | GameId | TeamPresetId | RoundId (as string for flexibility)
  localBlob: z.any(), // Original compressed image (Blob at runtime)
  localThumbnail: z.any(), // Thumbnail (128x128 or 256x256) (Blob at runtime)
  remoteUrl: z
    .string()
    .refine((val) => {
      try {
        new URL(val);
        return true;
      } catch {
        return false;
      }
    }, 'Must be a valid URL')
    .optional(), // Supabase CDN URL (when synced)
  remoteThumbnailUrl: z
    .string()
    .refine((val) => {
      try {
        new URL(val);
        return true;
      } catch {
        return false;
      }
    }, 'Must be a valid URL')
    .optional(), // Supabase thumbnail URL
  uploadStatus: ImageUploadStatusSchema,
  caption: z.string().max(500).optional(), // User-provided description
  uploadedAt: TimestampSchema, // ISO timestamp when image was added
  sessionId: SessionId.optional(), // For session/round images (privacy tracking)
  userId: UserId, // User who uploaded the image
});
export type ImageRecord = z.infer<typeof ImageRecordSchema> & {
  localBlob: Blob;
  localThumbnail: Blob;
};
